import { defineStore } from 'pinia';

import type { EmailSender } from '@stuut/api/models';
import { waitFor } from '@stuut/common/src/utils';

import { stuut } from '@/api';

interface State {
  emailSenders: EmailSender[];
  loading: boolean;
}

export const useEmailSenders = defineStore('emailSenders', {
  actions: {
    async fetchEmailSenders(force = false) {
      try {
        if (!force && (this.$state.emailSenders.length || this.$state.loading)) {
          await waitFor(() => !this.$state.loading, 250);
        } else {
          this.$patch({ loading: true });
          const senders = await stuut.emailSenders.list();
          this.$patch({ emailSenders: senders, loading: false });
        }

        return this.$state.emailSenders;
      } catch (error: any) {
        console.error(error);
        this.$patch({ loading: false });
        throw error;
      }
    },

    async removeEmailSender(emailSender: EmailSender) {
      const emailSenders = this.emailSenders.filter((sender) => sender.id !== emailSender.id);

      this.$patch({ emailSenders });

      return emailSenders;
    },

    patchEmailSender(emailSenderId: string, patch: Partial<EmailSender>) {
      this.$patch((state) => {
        const index = state.emailSenders.findIndex((s) => s.id === emailSenderId);
        if (index !== -1) {
          state.emailSenders[index] = { ...state.emailSenders[index], ...patch };
        }
      });
    },
  },
  getters: {
    getSenderById: (state) => {
      return (senderId: string) => state.emailSenders.find((sender) => sender.id === senderId);
    },
    visibleEmailSenders: (state) =>
      state.emailSenders.filter((sender) => !sender.is_paired_sendgrid_sender),
    globalInboxEmailSenders: (state) =>
      state.emailSenders.filter((sender) => sender.is_centralized_inbox),
  },
  state: (): State => ({
    emailSenders: [],
    loading: false,
  }),
});

export default useEmailSenders;
